import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import TextError from "../MultiStep/TextError";
import Modal from "react-modal";
Modal.setAppElement("#root");

const JoiningDetailsUpdate = () => {
  const accessToken = localStorage.getItem("accessToken");
  const { id } = useParams();
  const [employeeData, setEmployeeData] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [companyList, setCompanyList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [designationList, setDesignationList] = useState([]);
  const [reportingManagerList, setReportingManagerList] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();

  // Plain fetch helpers — always called with explicit IDs, never depend on state
  const fetchDesignations = (deptId) => {
    if (!deptId) return;
    axios
      .get(`${process.env.REACT_APP_BASE_URL}/departments/${deptId}/roles`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((response) => setDesignationList(response.data?.data || []))
      .catch((error) => console.error("Error fetching designations:", error));
  };

  const fetchDepartments = (companyName) => {
    if (!companyName) return Promise.resolve([]);
    return axios
      .get(`${process.env.REACT_APP_BASE_URL}/departments?company=${companyName}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((response) => {
        const data = response.data?.data || [];
        setDepartmentList(data);
        return data;
      })
      .catch((error) => console.error("Error fetching departments:", error));
  };

  // On mount: fetch employee, then immediately trigger department + designation fetch
  // using the values we get back from the response — no state dependency at all
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/employees/employeeInformation/${id}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const data = response.data?.data || [];
        setEmployeeData(data);

        if (data[0]) {
          const companyName = data[0]?.users?.roles[0]?.company[0]?.company_name || "";
          const deptId      = data[0]?.users?.roles[0]?.dept_id || "";
          const roleId      = data[0]?.users?.roles[0]?.id || "";

          setSelectedRoleId(roleId);

          // Call directly with values from response — no waiting for state
          if (companyName) fetchDepartments(companyName);
          if (deptId)      fetchDesignations(deptId);
        }
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchEmployeeData();
  }, [id]);

  // Fetch companies on mount
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BASE_URL}/companies`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((response) => setCompanyList(response.data?.data || []))
      .catch((error) => console.error("Error fetching companies:", error));
  }, []);

  // Fetch reporting managers on mount
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BASE_URL}/employees/managers`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((response) => setReportingManagerList(response.data?.data || []))
      .catch((error) => console.error("Error fetching managers:", error));
  }, []);

  // Validation
  const JoiningDetailsValidation = Yup.object({
    companyName:      Yup.string().required("Company Name is required"),
    department:       Yup.string().required("Department is required"),
    joining_date:     Yup.date().required("Joining Date is required"),
    reportingManager: Yup.string().required("Reporting Manager is required"),
    designation:      Yup.string().required("Designation is required"),
  });

  // Submit
  const handleSubmit = async (values) => {
    try {
      const companyId = companyList.find(
        (c) => c.company_name === values.companyName
      )?.id;

      const departmentId = departmentList.find(
        (d) => d.department_name === values.department
      )?.id;

      // Use list lookup first; fall back to selectedRoleId if user never changed designation
      const roleId =
        designationList.find((d) => d.role_name === values.designation)?.id ||
        selectedRoleId;

      const reportingManagerId = reportingManagerList.find(
        (m) => m.employee_full_name === values.reportingManager
      )?.employee_information_id;

      const payload = {
        id:                      employeeData[0].id,
        reporting_manager_id:    reportingManagerId,
        joining_date:            values.joining_date,
        access_card_expiry_date: values.access_card_expiry_date,
        work_hours:              values.work_hours,
        role_id:                 roleId,
        company_id:              companyId,
        department_id:           departmentId,
      };

      // console.log("Submitting payload:", payload);

      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/employees/joiningDetails`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error updating data:", error);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    navigate(`/employeedirectory/${id}`);
  };

  // Dropdown change handlers — cascade re-fetch on user interaction
  const handleInputChange = (event, type, formik) => {
    const { name, value } = event.target;

    if (type === "company") {
      formik.setFieldValue(name, value);
      formik.setFieldValue("department", "");
      formik.setFieldValue("designation", "");
      setDepartmentList([]);
      setDesignationList([]);
      setSelectedRoleId("");
      fetchDepartments(value);
    } else if (type === "department") {
      const deptObj = departmentList.find((d) => d.department_name === value);
      if (deptObj) {
        formik.setFieldValue(name, value);
        formik.setFieldValue("designation", "");
        setDesignationList([]);
        setSelectedRoleId("");
        fetchDesignations(deptObj.id);  // pass id directly, no state involved
      }
    }
  };

  // Initial form values from employee data
  const reportingManagerFullName = employeeData[0]?.reportingManager
    ? `${employeeData[0].reportingManager.first_name} ${employeeData[0].reportingManager.last_name}`.trim()
    : "";

  const initialValues = {
    companyName:             employeeData[0]?.users?.roles[0]?.company[0]?.company_name || "",
    department:              employeeData[0]?.users?.roles[0]?.department_name || "",
    joining_date:            employeeData[0]?.joining_date || "",
    reportingManager:        reportingManagerFullName,
    designation:             employeeData[0]?.users?.roles[0]?.role_name || "",
    work_hours:              employeeData[0]?.work_hours || "",
    access_card_expiry_date: employeeData[0]?.access_card_expiry_date || "",
  };

  return (
    <div className="board__layout">
      <div className="section-header">
        <h1>Update Joining Details</h1>
      </div>

      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={handleSubmit}
        validationSchema={JoiningDetailsValidation}
      >
        {(formik) => (
          <Form style={{ padding: "30px" }}>
            <div className="form-rows">

              {/* Company */}
              <div>
                <label htmlFor="companyName">Company Name</label>
                <Field
                  name="companyName"
                  as="select"
                  value={formik.values.companyName}
                  onChange={(e) => handleInputChange(e, "company", formik)}
                >
                  <option value="">Select Company</option>
                  {companyList.map((company) => (
                    <option key={company.id} value={company.company_name}>
                      {company.company_name}
                    </option>
                  ))}
                </Field>
                <ErrorMessage name="companyName" component={TextError} />
              </div>

              {/* Department */}
              <div>
                <label htmlFor="department">Department</label>
                <Field
                  name="department"
                  as="select"
                  value={formik.values.department}
                  onChange={(e) => handleInputChange(e, "department", formik)}
                >
                  <option value="">Select the Department</option>
                  {departmentList.map((department) => (
                    <option key={department.id} value={department.department_name}>
                      {department.department_name}
                    </option>
                  ))}
                </Field>
                <ErrorMessage name="department" component={TextError} />
              </div>
            </div>

            <div className="form-rows">

              {/* Joining Date */}
              <div>
                <label htmlFor="joining_date">Joining Date</label>
                <Field type="date" name="joining_date" id="joining_date" />
                <ErrorMessage name="joining_date" component={TextError} />
              </div>

              {/* Reporting Manager */}
              <div>
                <label htmlFor="reportingManager">Reporting Manager</label>
                <Field
                  name="reportingManager"
                  as="select"
                  value={formik.values.reportingManager}
                  onChange={(e) => formik.setFieldValue("reportingManager", e.target.value)}
                >
                  <option value="">Select Reporting Manager</option>
                  {reportingManagerList.map((manager) => (
                    <option
                      key={manager.employee_information_id}
                      value={manager.employee_full_name}
                    >
                      {`${manager.employee_full_name} - ${manager.employee_id}`}
                    </option>
                  ))}
                </Field>
                <ErrorMessage name="reportingManager" component={TextError} />
              </div>
            </div>

            <div className="form-rows">

              {/* Designation */}
              <div>
                <label htmlFor="designation">Designation</label>
                <Field
                  name="designation"
                  as="select"
                  value={formik.values.designation}
                  onChange={(e) => {
                    const roleName = e.target.value;
                    formik.setFieldValue("designation", roleName);
                    const roleId = designationList.find((d) => d.role_name === roleName)?.id;
                    if (roleId) setSelectedRoleId(roleId);
                  }}
                >
                  <option value="">Select Designation</option>
                  {designationList.map((designation) => (
                    <option key={designation.id} value={designation.role_name}>
                      {designation.role_name}
                    </option>
                  ))}
                </Field>
                <ErrorMessage name="designation" component={TextError} />
              </div>

              {/* Access Card Expiry */}
              <div>
                <label htmlFor="access_card_expiry_date">Access Card Expiry Date</label>
                <Field
                  type="date"
                  name="access_card_expiry_date"
                  id="access_card_expiry_date"
                />
              </div>
            </div>

            <div className="form-rows">
              {/* Work Hours */}
              <div>
                <label htmlFor="work_hours">Work Hours</label>
                <Field
                  type="text"
                  name="work_hours"
                  id="work_hours"
                  placeholder="Enter Employee Work Hours"
                />
              </div>
              <div></div>
            </div>

            <div className="form-btn-div" style={{ marginTop: "1rem" }}>
              <button
                type="button"
                className="btn-skip"
                onClick={() => navigate(`/employeedirectory/${id}`)}
              >
                Close
              </button>
              <button type="submit" className="form-btn-submit">
                Update
              </button>
            </div>
          </Form>
        )}
      </Formik>

      <Modal
        isOpen={showSuccessModal}
        onRequestClose={() => setShowSuccessModal(false)}
        contentLabel="Data Updated Successfully"
        className="react-modal-content"
        overlayClassName="react-modal-overlay"
      >
        <h5>Details Updated Successfully</h5>
        <button onClick={handleCloseModal}>OK</button>
      </Modal>
    </div>
  );
};

export default JoiningDetailsUpdate;